import { useTranslation } from "react-i18next";
import {
  Pressable,
  StyleSheet,
  View,
} from "react-native";

import { Modal } from "@/components/ui/app-modal";

import { ThemedText } from "@/components/themed-text";
import { BlurredBackdrop } from "@/components/ui/blurred-backdrop";
import { Radius, Spacing } from "@/constants/theme";
import { useTheme } from "@/hooks/use-theme";
import { useConfirmStore } from "@/state/confirm-store";

/**
 * The app's own confirmation dialog — every platform, every prompt.
 *
 * Web first (in place of `window.confirm` and its browser chrome), native
 * since the owner decision of 2026-08-21: the system alert was the one
 * surface the theme could not reach, an OS-styled interruption over an app
 * that styles everything else. `lib/dialog.ts` routes both `confirm()` and
 * `notify()` here on iOS and Android too, and no longer touches `Alert`.
 *
 * Mounted once at the root rather than per screen, because `confirm()` is
 * called from six of them and a dialog that only exists on the screen that
 * happens to have imported it is the same trap as the Alert it replaced.
 *
 * Dismissing — backdrop, Escape, Android back — resolves as **cancelled**.
 * There is no way out of this dialog that reads as agreement.
 */
export function ConfirmDialog() {
  const c = useTheme();
  useTranslation();
  const pending = useConfirmStore((state) => state.pending);
  const resolve = useConfirmStore((state) => state.resolve);

  // Mounted only while a question is pending, and the unmount is load-bearing.
  //
  // react-native-web appends a Modal's portal `<div>` to `document.body` when
  // the Modal COMPONENT mounts — not when it becomes visible (ModalPortal
  // renders unconditionally; only the inner animation div toggles). Every modal
  // gets the same `zIndex: 9999`, so among visible ones, DOM order decides.
  // Kept permanently mounted at the root, this dialog owned the first modal
  // slot in the whole document, and every screen-level Modal — the chat end
  // sheet, the photo viewer, the group and growth modals — mounted later and
  // painted over it. A confirm asked from inside any of them was invisible:
  // tapping Block did nothing until the sheet was closed by hand, and the
  // question then surfaced from behind it.
  //
  // Returning null until asked means the portal is appended at ASK time —
  // after whatever modal is currently open — so the question always lands on
  // top of the screen that raised it, including from inside modals that ask
  // without closing themselves first.
  if (pending === null) return null;

  return (
    <Modal
      visible
      transparent
      animationType="fade"
      statusBarTranslucent
      onRequestClose={() => resolve(false)}
    >
      <View style={styles.screen}>
        <BlurredBackdrop
          accessibilityLabel={pending?.cancelLabel}
          style={styles.backdrop}
          onPress={() => resolve(false)}
        />

        <View
          style={[
            styles.sheet,
            { backgroundColor: c.surface, borderColor: c.border },
          ]}
        >
          <ThemedText variant="subtitle" center>
            {pending?.title}
          </ThemedText>

          {pending?.message ? (
            <ThemedText variant="body" tone="secondary" center>
              {pending.message}
            </ThemedText>
          ) : null}

          <View style={styles.actions}>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={pending?.confirmLabel}
              onPress={() => resolve(true)}
              style={[
                styles.primaryBtn,
                {
                  backgroundColor: pending?.destructive ? c.danger : c.tint,
                },
              ]}
            >
              <ThemedText variant="bodyStrong" tone="onTint">
                {pending?.confirmLabel}
              </ThemedText>
            </Pressable>

            {/*
              A notice has nothing to decline. Offering Cancel next to OK on a
              message that only reports something asks a question that was
              never posed, and both answers would do the same thing.
            */}
            {pending?.kind === "confirm" ? (
              <Pressable
                accessibilityRole="button"
                accessibilityLabel={pending.cancelLabel}
                onPress={() => resolve(false)}
                style={[styles.secondaryBtn, { borderColor: c.border }]}
              >
                <ThemedText variant="bodyStrong">
                  {pending.cancelLabel}
                </ThemedText>
              </Pressable>
            ) : null}
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, alignItems: "center", justifyContent: "center" },
  backdrop: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  sheet: {
    width: "88%",
    maxWidth: 420,
    padding: Spacing.xl,
    gap: Spacing.md,
    alignItems: "center",
    borderRadius: Radius.xl,
    borderWidth: 1,
  },
  actions: { width: "100%", gap: Spacing.sm, marginTop: Spacing.sm },
  primaryBtn: {
    minHeight: 52,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: Radius.lg,
  },
  secondaryBtn: {
    minHeight: 50,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: Radius.lg,
    borderWidth: 1,
  },
});
